import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_APPEARANCE } from '../lib/appearance';
import { toast } from 'sonner';

// Надёжный фоллбек URL бэкенда для предотвращения NetworkError
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://fedyk-furniture-backend.onrender.com').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

// ==========================================
// 1. LANGUAGE & GEOLOCATION STORE
// ==========================================
export const useLanguageStore = create(
  persist(
    (set) => ({
      language: 'en',
      userCountry: 'BE', 
      setLanguage: (lang) => set({ language: lang }),
      
      detectLocationAndLanguage: async () => {
        try {
          const res = await fetch(`${API}/geo/detect`);
          if (res.ok) {
            const countryCode = await res.text();
            const cleanCountry = countryCode.trim().toUpperCase();
            
            let targetLang = 'en';
            if (cleanCountry === 'RO') targetLang = 'ro';
            if (cleanCountry === 'BE') targetLang = 'nl'; 

            set({ userCountry: cleanCountry, language: targetLang });
          }
        } catch (e) {
          console.error("Failed to auto-detect country and language:", e);
        }
      }
    }),
    { name: 'fedyk-language' }
  )
);

// ==========================================
// 2. APPEARANCE SYSTEM STORE
// ==========================================
export const useAppearanceStore = create(
  persist(
    (set) => ({
      config: DEFAULT_APPEARANCE || { images: {}, hero: { slides: [] }, colors: {}, typography: {}, logo: {} },
      loaded: true,
      fetchAppearance: async () => {
        const endpoints = [
          `${API}/site-settings/appearance`,
          `${API}/public/appearance`
        ];

        for (const url of endpoints) {
          try {
            const res = await fetch(url);
            if (res.ok) {
              const data = await res.json();
              const parsedConfig = data?.appearance || data?.published || data?.config || data?.draft || data;
              
              if (parsedConfig && (parsedConfig.images || parsedConfig.hero)) {
                set({ config: parsedConfig, loaded: true });
                return; 
              }
            }
          } catch (e) { /* noop */ }
        }
      }
    }),
    { name: 'fedyk-appearance-cache' }
  )
);

// ==========================================
// 3. SITE COPY STORE
// ==========================================
export const useCopyStore = create(
  persist(
    (set, get) => ({
      overrides: {},
      loaded: true,
      
      fetchCopy: async () => {
        const cacheBuster = Date.now();
        const copyEndpoints = [
          `${API}/site-settings/copy?t=${cacheBuster}`,
          `${API}/public/copy?t=${cacheBuster}`
        ];

        for (const url of copyEndpoints) {
          try {
            const res = await fetch(url);
            if (res.ok) {
              const data = await res.json();
              const parsedOverrides = data?.overrides || data?.strings || data || {};
              set({ overrides: parsedOverrides, loaded: true });
              return;
            }
          } catch (e) { /* noop */ }
        }
      },

      reloadCopy: async () => {
        const fetchCopyFunc = get().fetchCopy;
        if (fetchCopyFunc) {
          await fetchCopyFunc();
        }
      }
    }),
    { name: 'fedyk-copy-cache' }
  )
);

// ==========================================
// 4. LEAD CAPTURE MODAL STORE
// ==========================================
export const useLeadCaptureStore = create((set) => ({
  isOpen: false,
  source: null,
  open: (source = 'unknown') => set({ isOpen: true, source }),
  close: () => set({ isOpen: false }),
}));

export const openLeadModal = (source = 'unknown') => {
  try {
    useLeadCaptureStore.getState().open(source);
  } catch (e) {
    window.dispatchEvent(new CustomEvent('openLeadModal', { detail: source }));
  }
};

// ==========================================
// 5. AUTHENTICATION STORE (ПОДДЕРЖКА 2FA И ПРЯМОГО ВХОДА)
// ==========================================
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password, otp_code = null) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, otp_code }),
          });
          const data = await res.json();
          if (!res.ok) {
            const msg = Array.isArray(data.detail) ? data.detail[0].msg : data.detail;
            throw new Error(msg || 'Login failed');
          }

          // Если от администратора требуется 2FA-код
          if (data.status === '2fa_required') {
            set({ isLoading: false });
            return data;
          }

          // Успешный вход по логину/паролю или валидному 2FA
          set({
            user: data.user,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });
          const data = await res.json();
          if (!res.ok) {
            const msg = Array.isArray(data.detail) ? data.detail[0].msg : data.detail;
            throw new Error(msg || 'Registration failed');
          }

          if (data.access_token) {
            set({ user: data.user, token: data.access_token, isAuthenticated: true, isLoading: false });
          } else {
            set({ isLoading: false });
          }
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: () => set({ user: null, token: null, isAuthenticated: false }),

      refreshUser: async () => {
        const { token } = get();
        if (!token) return null;
        try {
          const res = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const user = await res.json();
            set({ user, isAuthenticated: true });
            return user;
          }
          if (res.status === 401) set({ user: null, token: null, isAuthenticated: false });
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
        return null;
      },

      getAuthHeaders: () => {
        const { token } = get();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
    { name: 'fedyk-auth' }
  )
);

// ==========================================
// 6. WISHLIST STORE
// ==========================================
export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,

      fetchWishlist: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;
        set({ isLoading: true });
        try {
          const res = await fetch(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            set({ items: data.items || [], isLoading: false });
          }
        } catch (error) {
          set({ isLoading: false });
        }
      },

      addToWishlist: async (productId) => {
        const { token } = useAuthStore.getState();
        if (!token) return false;
        try {
          const res = await fetch(`${API}/wishlist/${productId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            set((state) => ({ items: [...state.items, { product_id: productId, added_at: new Date().toISOString() }] }));
            return true;
          }
        } catch (error) { /* noop */ }
        return false;
      },

      removeFromWishlist: async (productId) => {
        const { token } = useAuthStore.getState();
        if (!token) return false;
        try {
          const res = await fetch(`${API}/wishlist/${productId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            set((state) => ({ items: state.items.filter((i) => i.product_id !== productId) }));
            return true;
          }
        } catch (error) { /* noop */ }
        return false;
      },

      isInWishlist: (productId) => get().items.some((i) => i.product_id === productId),
    }),
    { name: 'fedyk-wishlist' }
  )
);

// ==========================================
// 7. CART / QUOTE STORE
// ==========================================
export const useQuoteStore = create(
  persist(
    (set, get) => ({
      items: [],
      addItem: (product, quantity = 1) => {
        set((state) => {
          const existing = state.items.find((i) => i.product.id === product.id);
          if (existing) {
            return { items: state.items.map((i) => i.product.id === product.id ? { ...i, quantity: i.quantity + quantity } : i) };
          }
          return { items: [...state.items, { product, quantity }] };
        });
      },
      removeItem: (productId) => set((state) => ({ items: state.items.filter((i) => i.product.id !== productId) })),
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) { get().removeItem(productId); return; }
        set((state) => ({ items: state.items.map((i) => i.product.id === productId ? { ...i, quantity } : i) }));
      },
      clearItems: () => set({ items: [] }),
      getTotal: () => get().items.reduce((sum, i) => sum + i.product.price * i.quantity, 0),
      getItemCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
    }),
    { name: 'fedyk-quote' }
  )
);

// ==========================================
// 7b. B2B PROJECT SPECIFICATION STORE
// ==========================================
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const projectHeaders = () => {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const useProjectStore = create((set, get) => ({
  projects: [],
  activeProjectId: null,
  loading: false,

  fetchProjects: async () => {
    if (!useAuthStore.getState().token) return;
    set({ loading: true });
    try {
      const res = await fetch(`${API}/b2b/projects`, { headers: projectHeaders() });
      if (res.ok) {
        const data = await res.json();
        const projects = data.projects || [];
        set((s) => ({
          projects,
          activeProjectId: s.activeProjectId && projects.some((p) => p.id === s.activeProjectId) ? s.activeProjectId : projects[0]?.id || null,
        }));
      }
    } catch (e) { /* noop */ } finally { set({ loading: false }); }
  },

  createProject: async (name) => {
    try {
      const res = await fetch(`${API}/b2b/projects`, {
        method: 'POST',
        headers: projectHeaders(),
        body: JSON.stringify({ name: (name || 'Untitled project').trim(), items: [] }),
      });
      if (!res.ok) return null;
      const project = await res.json();
      set((s) => ({ projects: [project, ...s.projects], activeProjectId: project.id }));
      return project.id;
    } catch (e) { return null; }
  },

  setActiveProject: (projectId) => set({ activeProjectId: projectId }),

  _patchProject: async (projectId, patch) => {
    try {
      const res = await fetch(`${API}/b2b/projects/${projectId}`, {
        method: 'PATCH',
        headers: projectHeaders(),
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? updated : p)) }));
      }
    } catch (e) { /* noop */ }
  },

  renameProject: (projectId, name) => {
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? { ...p, name } : p)) }));
    get()._patchProject(projectId, { name });
  },

  removeProject: async (projectId) => {
    set((s) => {
      const projects = s.projects.filter((p) => p.id !== projectId);
      return { projects, activeProjectId: s.activeProjectId === projectId ? projects[0]?.id || null : s.activeProjectId };
    });
    try { await fetch(`${API}/b2b/projects/${projectId}`, { method: 'DELETE', headers: projectHeaders() }); } catch (e) { /* noop */ }
  },

  addItemToProject: (projectId, item) => {
    const project = get().projects.find((p) => p.id === projectId);
    if (!project) return;
    const items = [...(project.items || []), { line_id: uid(), quantity: 1, configuration: {}, ...item }];
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? { ...p, items } : p)) }));
    get()._patchProject(projectId, { items });
  },

  updateItem: (projectId, lineId, patch) => {
    const project = get().projects.find((p) => p.id === projectId);
    if (!project) return;
    const items = project.items.map((it) => (it.line_id === lineId ? { ...it, ...patch } : it));
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? { ...p, items } : p)) }));
    get()._patchProject(projectId, { items });
  },

  removeItem: (projectId, lineId) => {
    const project = get().projects.find((p) => p.id === projectId);
    if (!project) return;
    const items = project.items.filter((it) => it.line_id !== lineId);
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? { ...p, items } : p)) }));
    get()._patchProject(projectId, { items });
  },

  downloadOffer: async (projectId, mode = 'client', lang = 'en') => {
    const url = `${API}/b2b/projects/${projectId}/specification-document?mode=${mode}&lang=${lang}`;
    
    const res = await fetch(url, {
      method: 'GET',
      headers: projectHeaders(),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      const serverDetail = errData?.detail || 'Unknown internal crash';
      toast.error(`DIAGNOSTIC — HTTP ${res.status}: ${JSON.stringify(serverDetail)}`, { duration: 10000 });
      throw new Error(`Offer generation failed: ${res.status}`);
    }

    const blob = await res.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    
    const filePrefix = mode === 'trade' ? 'Trade_Pricing' : 'Client_Proposal';
    a.download = `Project_${filePrefix}_${projectId}.pdf`;
    
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  },
}));

// ==========================================
// 8. HELPERS & GLOBAL EXPORTS
// ==========================================
export const getLocalized = (obj, lang = 'en') => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  if (obj[lang] && typeof obj[lang] === 'string' && obj[lang].trim() !== '') {
    return obj[lang];
  }
  if (obj['en'] && typeof obj['en'] === 'string' && obj['en'].trim() !== '') {
    return obj['en'];
  }
  const availableLangs = Object.keys(obj).filter(key => obj[key] && String(obj[key]).trim() !== '');
  if (availableLangs.length > 0) {
    return obj[availableLangs[0]];
  }
  return '';
};

export const apiFetch = async (endpoint, options = {}) => {
  const { token } = useAuthStore.getState();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${API}${endpoint}`, { ...options, headers });
  const data = await res.json().catch(() => null);
  
  if (!res.ok) {
    const detail = data?.detail;
    const msg = Array.isArray(detail) ? detail[0].msg : detail;
    throw new Error(msg || 'Request failed');
  }
  return data;
};

if (typeof window !== 'undefined') {
  window.useLanguageStore = useLanguageStore;
  window.useAppearanceStore = useAppearanceStore;
  window.useCopyStore = useCopyStore;
}
